import { Button, Card, Flex, Space, Spin, Table, TableColumnsType, Typography } from "antd";
import { TRefProvider } from "api/services/models/types";
import { getRefProvidersForAccount } from "api/services/RefProviderService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { ReactComponent as AddSquareIcon } from "Icons/vuesax/add-square.svg";
import { ReactComponent as EditIcon } from "Icons/vuesax/edit-2.svg";
import { ReactComponent as ProviderIcon } from "Icons/vuesax/user-octagon.svg";
import { useEffect, useState } from "react";
import AddOrUpdateRefProvider from "./AddOrUpdateRefProvider";

const composeColumns = (
  onEditRefProvider: (refProvider: TRefProvider) => void
): TableColumnsType<TRefProvider> => [
  {
    title: "",
    render: () => <ProviderIcon className="provider-icon"></ProviderIcon>
  },
  {
    title: "Practice Name",
    dataIndex: "practiceName",
    sorter: (s1, s2) => s1?.practiceName?.localeCompare(s2.practiceName),
  },
  {
    title: "Provider Name",
    dataIndex: "rpName"
  },
  {
    title: "Address 1",
    dataIndex: "address1"
  },
  {
    title: "Suite",
    dataIndex: "address2"
  },
  {
    title: "City",
    dataIndex: "city"
  },
  {
    title: "State",
    dataIndex: "state"
  },
  {
    title: "Fax",
    dataIndex: "fax"
  },
  {
    title: "Ext. Provider Id",
    dataIndex: "extProviderId"
  },
  {
    title: "",
    render: (_: any, record: TRefProvider) => (
      <EditIcon onClick={() => onEditRefProvider(record)} className="settings-edit-icon" />
    )
  }
];

const ManageRefProviders = () => {
  const { accountId } = useTDAuthentication();
  const [refProviders, setRefProviders] = useState<TRefProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AxiosError | null>();
  const [editState, setEditState] = useState<{
    showModal: boolean;
    refProvider?: TRefProvider;
  }>();
  const [tableKey, setTableKey] = useState(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      getRefProvidersForAccount(accountId)
        .then(setRefProviders)
        .then(() => setTableKey(k => k + 1))
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [accountId]);

  const closeModal = (refProvider?: TRefProvider) => {
    setEditState({ showModal: false });
    if (refProvider) {
      setRefProviders(pvds => {
        const index = pvds.findIndex(p => p.rpId === refProvider.rpId);
        if (index === -1) {
          pvds?.push(refProvider);
        } else {
          pvds?.splice(index, 1, refProvider);
        }
        return [...pvds];
      });
    }
  };

  return (
    <Card
      className="default-border"
      styles={{ body: { padding: "0" } }}
      style={{
        padding: "16px"
      }}>
        <Flex wrap gap="middle" align="start" justify="space-between" style={{ marginBottom: "16px" }}>
          <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
            Manage Referring Providers
          </Typography.Title>
          <Space size="small" direction="vertical" style={{ margin: 0 }} align="end">
            <Button
              type="primary"
              size="middle"
              className="default-button primary-btn"
              icon={<AddSquareIcon className="default-icon" />}
              iconPosition={"start"}
              title="Add New Referring Provider"
              onClick={() => setEditState({ showModal: true })}>
              <span>Add New Referring Provider</span>
            </Button>
            <span
              style={{
                fontSize: ".75rem"
              }}>{`Total Referring Providers: ${refProviders?.length}`}</span>
          </Space>
        </Flex>
      {loading && <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Loading, please wait..." />
        </Flex>}
      {error && <ErrorViewer error={error} />}
      {!loading &&
      <Card styles={{body:{padding:"0"}}} className="card-style">
      <Table<TRefProvider>
        key={tableKey}
        columns={composeColumns(refProvider => setEditState({ showModal: true, refProvider }))}
        dataSource={refProviders.sort((a, b) => a?.practiceName?.localeCompare(b.practiceName))}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: {
            showSearch: false
          },
          pageSizeOptions: [5, 10, 20, 50, 100],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size || 10);
          }
        }}
        rowKey={"rpId"}
        className="table-style"
            components={{
              body: {
                cell: (props: any) => (
                  <td
                    {...props}
                    style={{
                      borderBottomWidth: "1px",
                      borderBottomColor: "#f0f0f0",
                      borderBottomStyle: "solid",
                      fontWeight: "500"
                    }}
                  />
                )
              }
            }}
            style={{ border: "none" }}
      />
      </Card>
      }

      {editState?.showModal && (
        <AddOrUpdateRefProvider refProvider={editState.refProvider} onClose={closeModal} />
      )}
    </Card>
  );
};

export default ManageRefProviders;
